import { useMemo } from 'react';
import { Box, styled } from '@material-ui/core';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { Product } from '@setvi/shared/interfaces';
import {
  SRoundedContainer,
  SAccordion,
  SButton,
  SText
} from '@setvi/shared/components';
import { distances } from '@setvi/shared/styles';
import { useDialog } from '@setvi/shared/providers';

import { AILoader } from 'pages/products/components';
import {
  loadingSalesEnablementMessages,
  creatingSalesEnablementMessages
} from 'pages/products/components/ai/loader/messages';

import { useSalesEnablement } from './hook';
import Metadata from './components/metadata';
import { ContentTitleContainer } from '../product-info';
import { Container, MetadataMessage, ScrollableWrapper } from './styled';
import { NoSalesEnablement, SalesKitFeedback, Sidebar } from './components';

const Wrapper = styled(Box)({
  borderRadius: 16,
  padding: distances.px.base,
  border: '1px solid #EEF0F2'
});

export interface SalesEnablementProps {
  product: Product;
}

export const SalesEnablement = ({ product }: SalesEnablementProps) => {
  const flags = useFlags();

  const { openDialog, closeDialog } = useDialog();
  const {
    items,
    errorCode,
    activeItem,
    metadataFetched,
    initalLoading,
    expanded,
    isDisabled,
    isFetching,
    isToggleLoading,
    metadataErrorCode,
    regenerateAll,
    handleItemClick,
    handleToggle,
    handleContentClick,
    handleRegeneration
  } = useSalesEnablement({ product });

  const currentItem = useMemo(() => {
    const res = items?.find(item => item._id === activeItem);
    return res;
  }, [items, activeItem]);

  const sortedArr = useMemo(
    () => items?.sort((a, b) => a.order - b.order),
    [items]
  );

  const salesEnablementMessages = useMemo(
    () =>
      isFetching
        ? loadingSalesEnablementMessages
        : creatingSalesEnablementMessages,
    [isFetching]
  );

  if (initalLoading)
    return (
      <Container>
        <AILoader messages={salesEnablementMessages} />
        {metadataFetched && (
          <MetadataMessage>
            <SText size="sm">Metadata Fetched 🎉</SText>
          </MetadataMessage>
        )}
      </Container>
    );

  if (!items?.length && !currentItem) {
    return (
      <Container>
        <ContentTitleContainer
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
          <SText title="Sales Kit" weight="semibold" />

          <Box display="flex" gridGap="8px">
            {!!flags?.productMetadata && (
              <SButton
                onClick={handleToggle}
                loading={isToggleLoading}
                disabled={isToggleLoading}
                style={{
                  minHeight: 0,
                  borderRadius: 120
                }}>
                {isDisabled ? 'Enable' : 'Disable'}
              </SButton>
            )}
            <SalesKitFeedback product={product} />
          </Box>
        </ContentTitleContainer>

        <NoSalesEnablement
          disabled={isDisabled}
          errorCode={errorCode}
          metadataError={!!metadataErrorCode}
          onRetry={() => regenerateAll()}
        />
      </Container>
    );
  }

  return (
    <Container>
      <ContentTitleContainer
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
        <SText title="Sales Kit" weight="semibold" />
        <Box display="flex" gridGap="8px">
          {!!flags?.productMetadata && (
            <>
              <SButton
                onClick={handleToggle}
                loading={isToggleLoading}
                disabled={isToggleLoading}
                style={{
                  minHeight: 0,
                  borderRadius: 120
                }}>
                {isDisabled ? 'Enable' : 'Disable'}
              </SButton>
              <SButton
                variant="outlined"
                onClick={() =>
                  openDialog(
                    <Metadata
                      onClose={() => closeDialog('product-metadata')}
                      handleRegeneration={regenerateAll}
                      product={product}
                    />,
                    {
                      id: 'product-metadata'
                    }
                  )
                }
                style={{
                  minHeight: 0,
                  borderRadius: 120
                }}>
                Product Metadata
              </SButton>
            </>
          )}
          <SalesKitFeedback product={product} />
        </Box>
      </ContentTitleContainer>
      <Box display="flex" overflow="hidden" flex={1} pb={2} gridGap="16px">
        <Wrapper overflow="hidden auto" minWidth={250}>
          <Sidebar
            items={sortedArr}
            activeItem={activeItem}
            setActiveItem={handleItemClick}
          />
        </Wrapper>

        {!!currentItem && (
          <>
            {!!currentItem?.content?.length && (
              <ScrollableWrapper activeItem={activeItem}>
                {currentItem?.content?.map(item => (
                  <SRoundedContainer
                    key={item?.id}
                    mb={2}
                    style={{ padding: '0 16px' }}>
                    <SAccordion
                      key={item.name}
                      title={item.name}
                      content={
                        <div
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{ __html: item?.content }}
                        />
                      }
                      expanded={expanded === item.name}
                      onClick={name => {
                        handleContentClick(name, currentItem?.name || '');
                      }}
                    />
                  </SRoundedContainer>
                ))}
              </ScrollableWrapper>
            )}

            {currentItem?.loading && (
              <AILoader messages={salesEnablementMessages} />
            )}

            {!currentItem?.content?.length && !currentItem?.loading && (
              <NoSalesEnablement
                disabled={isDisabled}
                errorCode={errorCode}
                metadataError={!!metadataErrorCode}
                onRetry={() => handleRegeneration(activeItem || '')}
              />
            )}
          </>
        )}
      </Box>
    </Container>
  );
};
